import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import { inputCls, selectCls, cls } from "../ui";
import { FileBarChart2, Heart, Play, Search } from "lucide-react";

const U = "/ujms";
const MODULES = ["Case", "Hearing", "Warrant", "Financial", "Arrest", "Incident", "Booking", "Person", "System"];

export default function ReportLibrary() {
  const [reports, setReports] = useState([]);
  const [q, setQ] = useState("");
  const [mods, setMods] = useState([]);
  const [favOnly, setFavOnly] = useState(false);
  const [sort, setSort] = useState("name");
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get(`${U}/reports/catalog`).then((r) => setReports(r.data.reports)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    reports.forEach((r) => { c[r.module] = (c[r.module] || 0) + 1; });
    return c;
  }, [reports]);

  const rows = useMemo(() => {
    let out = reports;
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((r) => r.name.toLowerCase().includes(s) || r.description.toLowerCase().includes(s));
    }
    if (mods.length) out = out.filter((r) => mods.includes(r.module));
    if (favOnly) out = out.filter((r) => r.favorite);
    return [...out].sort((a, b) => sort === "module"
      ? a.module.localeCompare(b.module) || a.name.localeCompare(b.name)
      : a.name.localeCompare(b.name));
  }, [reports, q, mods, favOnly, sort]);

  const toggleFav = async (r) => {
    try {
      const res = await api.post(`${U}/reports/catalog/${r.slug}/favorite`);
      toast.success(res.data.favorite ? `${r.name} added to favorites` : `${r.name} removed from favorites`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="report-library-page">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Search Reports</h1>
        <button onClick={() => setFavOnly((v) => !v)} data-testid="rl-fav-filter"
          className={cls("inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm border transition-colors",
            favOnly ? "bg-ujms-dark text-white border-ujms-dark" : "border-slate-300 text-slate-600 hover:border-ujms-dark hover:text-ujms-dark")}>
          <Heart className={cls("w-4 h-4", favOnly && "fill-current")} /> Favorites
        </button>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <span className="font-semibold text-slate-600">Sort By</span>
          <select className={`${selectCls} w-44`} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="rl-sort">
            <option value="name">Report Name</option>
            <option value="module">Module</option>
          </select>
        </div>
      </div>

      <div className="flex gap-5 items-start">
        {/* Filters rail */}
        <aside className="w-64 shrink-0 border border-[#d5dae8] rounded-sm" data-testid="rl-filters">
          <div className="px-4 py-3 border-b border-[#d5dae8] text-[15px] font-bold text-slate-800">Filters</div>
          <div className="p-4 space-y-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Agency</div>
              <select className={selectCls} data-testid="rl-agency">
                <option>Circuit Court of Cook County</option>
              </select>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Search</div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={`${inputCls} pl-8`} placeholder="Report name or keyword" value={q}
                  onChange={(e) => setQ(e.target.value)} data-testid="rl-search" />
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Module</div>
              {MODULES.filter((m) => counts[m]).map((m) => (
                <label key={m} className="flex items-center gap-2 py-1 text-sm font-medium text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={mods.includes(m)}
                    onChange={(e) => setMods(e.target.checked ? [...mods, m] : mods.filter((x) => x !== m))}
                    data-testid={`rl-module-${m.toLowerCase()}`} />
                  {m} <span className="ml-auto text-xs text-slate-400 font-mono">{counts[m]}</span>
                </label>
              ))}
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="flex-1 min-w-0" data-testid="rl-results">
          <div className="text-sm text-slate-500 font-medium mb-2">{rows.length} report(s)</div>
          {rows.map((r) => (
            <div key={r.slug} className="flex items-start gap-4 border-b border-[#e4e7f2] py-4" data-testid={`rl-row-${r.slug}`}>
              <FileBarChart2 className="w-9 h-9 text-ujms-dark shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <button onClick={() => navigate(r.to)} data-testid={`rl-open-${r.slug}`}
                  className="text-[16px] font-bold text-ujms-dark hover:underline text-left">{r.name}</button>
                <div className="text-xs font-semibold text-slate-500 mt-0.5">
                  Agency: Circuit Court of Cook County&nbsp;&nbsp;|&nbsp;&nbsp;Module: {r.module}
                </div>
                <div className="text-sm text-slate-600 mt-1">{r.description}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-semibold text-slate-500">Type: {r.type}</div>
                <div className="text-xs font-semibold text-slate-500 mt-0.5">Created By: {r.created_by}</div>
                <div className="flex items-center justify-end gap-1 mt-2">
                  <button onClick={() => toggleFav(r)} aria-label={`Favorite ${r.name}`} data-testid={`rl-fav-${r.slug}`}
                    className={cls("p-1.5 rounded-sm hover:bg-slate-100", r.favorite ? "text-[#d93025]" : "text-slate-400")}>
                    <Heart className={cls("w-4.5 h-4.5 w-5 h-5", r.favorite && "fill-current")} />
                  </button>
                  <button onClick={() => navigate(r.to)} aria-label={`Run ${r.name}`} data-testid={`rl-run-${r.slug}`}
                    className="p-1.5 rounded-sm text-ujms-dark hover:bg-slate-100">
                    <Play className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {!rows.length && <div className="text-sm text-slate-400 py-8">No reports match the current filters.</div>}
        </div>
      </div>
    </div>
  );
}
